/* ──────────────────────────────────────────────────────────────
   pivot.ts – helpers de transformación para el informe semanal
   ────────────────────────────────────────────────────────────── */
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);

/* ─── Tipos ─── */
export interface WeekRow {
  profesor: string;
  weekKey: string; // 2025-W18
  weekLabel: string; // 05-May → 11-May
  horas: number;
}

/* ─── Helper rango visual ─── */
function rangeLabel(monday: dayjs.Dayjs): string {
  const sunday = monday.add(6, "day");
  return `${monday.format("DD-MMM")} → ${sunday.format("DD-MMM")}`;
}

/* ─── Lista continua de semanas entre dos fechas ─── */
export function getWeeksBetween(desde: string, hasta: string) {
  const firstMon = dayjs(desde).startOf("isoWeek");
  const lastSun = dayjs(hasta).endOf("isoWeek");

  const keys: string[] = [];
  const labels: string[] = [];

  for (let d = firstMon.clone(); d.isSameOrBefore(lastSun, "day"); d = d.add(1, "week")) {
    const key = `${d.isoWeekYear()}-W${String(d.isoWeek()).padStart(2, "0")}`;
    keys.push(key);
    labels.push(rangeLabel(d));
  }
  return { keys, labels }; // ej.: ['2025-W18', …], ['05-May → 11-May', …]
}

/* ─── Pivot ─── */
export function pivotWeeks(rows: WeekRow[], weekKeys: string[], weekLabels: string[]) {
  const profSeries = new Map<string, number[]>();

  rows.forEach((r) => {
    const idx = weekKeys.indexOf(r.weekKey);
    if (idx === -1) return; // fuera del rango seleccionado
    if (!profSeries.has(r.profesor)) {
      profSeries.set(r.profesor, Array(weekKeys.length).fill(0));
    }
    profSeries.get(r.profesor)![idx] += r.horas;
  });

  const data = [...profSeries.entries()].map(([profesor, serie]) => {
    const obj: Record<string, number | string> = { profesor };
    serie.forEach((h, i) => {
      obj[weekLabels[i]] = h;
    });
    return obj as { profesor: string } & Record<string, number>;
  });

  return { weeks: weekLabels, data };
}
